import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'
import { Goal, CreateGoalRequest, UpdateGoalRequest } from '../types'
import * as goalService from '../services/goals'

interface GoalsState {
  // State
  goals: Goal[]
  loading: boolean
  error: string | null
  isCreating: boolean
  isUpdating: boolean
  isImporting: boolean

  // Actions
  fetchGoals: (studentId?: string) => Promise<void>
  fetchGoalsByStudent: (studentId: string) => Promise<void>
  addGoal: (goalData: CreateGoalRequest) => Promise<Goal>
  bulkImportGoals: (
    goals: Array<{
      studentId: string
      firstName: string
      lastName: string
      category: string
      area: string
      description: string
      objectives: string
    }>,
  ) => Promise<{
    successful: number
    failed: number
    studentsCreated: number
    errors: any[]
  }>
  editGoal: (id: string, goalData: UpdateGoalRequest) => Promise<Goal>
  removeGoal: (id: string) => Promise<void>
  clearError: () => void
  reset: () => void
}

const initialState = {
  goals: [],
  loading: false,
  error: null,
  isCreating: false,
  isUpdating: false,
  isImporting: false,
}

export const useGoalsStore = create<GoalsState>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,

        fetchGoals: async (studentId?: string) => {
          try {
            set({ loading: true, error: null })
            const response = await goalService.getGoals(studentId)
            set({
              goals: response.data,
              loading: false,
            })
          } catch (error: any) {
            console.error('Error fetching goals:', error)
            set({
              error: 'Failed to fetch goals',
              loading: false,
            })
          }
        },

        fetchGoalsByStudent: async (studentId: string) => {
          try {
            set({ loading: true, error: null })
            const response = await goalService.getGoalsByStudent(studentId)
            set({
              goals: response.data,
              loading: false,
            })
          } catch (error: any) {
            console.error('Error fetching goals for student:', error)
            set({
              error: 'Failed to fetch goals',
              loading: false,
            })
          }
        },

        addGoal: async (goalData: CreateGoalRequest) => {
          try {
            set({ isCreating: true, error: null })
            const response = await goalService.createGoal(goalData)
            set({ isCreating: false })
            // Reload goals after successful creation
            await get().fetchGoals(goalData.student)
            return response.data
          } catch (error: any) {
            console.error('Error creating goal:', error)
            set({
              error: error.response?.data?.error || 'Failed to create goal',
              isCreating: false,
            })
            throw error
          }
        },

        bulkImportGoals: async (goals) => {
          try {
            set({ isImporting: true, error: null })
            const response = await goalService.bulkImportGoals(goals)
            set({ isImporting: false })
            // Reload goals after successful import
            await get().fetchGoals()
            return response.data
          } catch (error: any) {
            console.error('Error importing goals:', error)
            set({
              error: error.response?.data?.message || 'Failed to import goals',
              isImporting: false,
            })
            throw error
          }
        },

        editGoal: async (id: string, goalData: UpdateGoalRequest) => {
          try {
            set({ isUpdating: true, error: null })
            const response = await goalService.updateGoal(id, goalData)
            set({ isUpdating: false })
            // Reload goals after successful update
            const currentGoals = get().goals
            const updatedGoal = currentGoals.find((g) => g._id === id)
            if (updatedGoal) {
              await get().fetchGoals(updatedGoal.student as string)
            }
            return response.data
          } catch (error: any) {
            console.error('Error updating goal:', error)
            set({
              error: error.response?.data?.error || 'Failed to update goal',
              isUpdating: false,
            })
            throw error
          }
        },

        removeGoal: async (id: string) => {
          try {
            set({ loading: true, error: null })
            await goalService.deleteGoal(id)
            // Remove from local state
            set((state) => ({
              goals: state.goals.filter((g) => g._id !== id),
              loading: false,
            }))
          } catch (error: any) {
            console.error('Error deleting goal:', error)
            set({
              error: error.response?.data?.error || 'Failed to delete goal',
              loading: false,
            })
            throw error
          }
        },

        clearError: () => {
          set({ error: null })
        },

        reset: () => {
          set(initialState)
        },
      }),
      {
        name: 'goals-store',
      },
    ),
  ),
)
